/**
 * Componente de registro de empresas
 * Formulario completo para registro de nuevas empresas en el sistema
 * @author sebastian salinas, Alejandro Ángel y Diego Alejandro
 * @version 2.5.0
 */   

import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import CountrySelector from "components/Api/CountrySelector";
import bgImage from "assets/images/Login/imagen1_optimizada.webp";
import { CITIES_BY_COUNTRY, DEPARTMENTS_BY_COUNTRY, CITIES_BY_DEPARTMENT } from "../../../utils/latinAmericanCountries";
import registerCompanyService from "services/registerCompanyService";
import { MenuItem } from "@mui/material";

const INDICATIVOS_PAIS = {
  "Argentina": "+54",
  "Bolivia": "+591",
  "Brasil": "+55",
  "Chile": "+56",
  "Colombia": "+57",
  "Costa Rica": "+506",
  "Cuba": "+53",
  "República Dominicana": "+1-809",
  "Ecuador": "+593",
  "El Salvador": "+503",
  "Guatemala": "+502",
  "Honduras": "+504",
  "México": "+52",
  "Nicaragua": "+505",
  "Panamá": "+507",
  "Paraguay": "+595",
  "Perú": "+51",
  "Uruguay": "+598",
  "Venezuela": "+58",
  "Puerto Rico": "+1-787"
};
// Términos y condiciones completos
const TERMINOS_Y_CONDICIONES = `TÉRMINOS Y CONDICIONES DE USO

Aplicación Quadrante H

El presente documento establece los Términos y Condiciones que regulan el acceso y uso de la aplicación Quadrante H (en adelante "la Plataforma"), propiedad de Koop Tecnológica SAS Bic. Al registrarse, acceder y/o utilizar la Plataforma, el Usuario declara haber leído, entendido y aceptado íntegramente las disposiciones contenidas en este documento.

1. Objeto

La Plataforma tiene como finalidad proporcionar herramientas de evaluación psicológica y de ajuste al cargo que sirven de apoyo a empresas en sus procesos de selección de personal.

2. Usuarios autorizados

El acceso a la Plataforma se limita a empresas, organizaciones y personas jurídicas debidamente registradas. Los Usuarios son responsables de la confidencialidad de sus credenciales de acceso y de toda actividad realizada bajo su cuenta.

3. Propiedad intelectual

Todos los contenidos, metodologías, pruebas, diseños, software y materiales asociados a la Plataforma son propiedad exclusiva de Quadrante H y se encuentran protegidos por la normativa vigente en materia de derechos de autor y propiedad intelectual. Queda prohibida su reproducción, distribución, cesión, transformación o utilización para fines distintos a los aquí autorizados, sin el consentimiento previo y por escrito del titular.

4. Confidencialidad y protección de datos

El Usuario se compromete a utilizar la Plataforma respetando la legislación aplicable en materia de protección de datos personales. La información de los candidatos será tratada exclusivamente con fines de evaluación y el Usuario será responsable de contar con el consentimiento informado de los evaluados.

5. Limitación de responsabilidad

Quadrante H no se responsabiliza por las decisiones de contratación adoptadas por las empresas usuarias, ni garantiza la idoneidad absoluta de los candidatos evaluados. Los resultados constituyen únicamente un insumo de apoyo para la toma de decisiones organizacionales.

6. Modificaciones

Quadrante H se reserva el derecho de modificar, actualizar o complementar en cualquier momento los presentes Términos y Condiciones. Las modificaciones entrarán en vigor desde su publicación en la Plataforma, siendo responsabilidad del Usuario revisarlos periódicamente.

7. Aceptación

El registro y utilización de la Plataforma implica la aceptación expresa y sin reservas de los presentes Términos y Condiciones.`;

// Opciones de sectores empresariales disponibles
const Nombre_area = [
  { value: "recursos_humanos", label: "Recursos Humanos" },
  { value: "finanzas_contabilidad", label: "Finanzas / Contabilidad" },
  { value: "comercial_ventas", label: "Comercial / Ventas" },
  { value: "marketing_publicidad", label: "Marketing / Publicidad" },
  { value: "tecnologia_sistemas_it", label: "Tecnología / Sistemas / IT" },
  { value: "produccion_operaciones", label: "Producción / Operaciones" },
  { value: "logistica_distribucion_transporte", label: "Logística / Distribución / Transporte" },
  { value: "atencion_cliente_call_center", label: "Atención al Cliente / Call Center" },
  { value: "servicio_cliente_presencial", label: "Servicio al Cliente Presencial" },
  { value: "calidad_control_procesos", label: "Calidad / Control de Procesos" },
  { value: "proyectos_innovacion", label: "Proyectos / Innovación" },
  { value: "legal_juridico", label: "Legal / Jurídico" },
  { value: "salud_seguridad_trabajo", label: "Salud y Seguridad en el Trabajo" },
  { value: "investigacion_desarrollo", label: "Investigación y Desarrollo (I+D)" },
  { value: "educacion_formacion", label: "Educación / Formación" },
  { value: "direccion_alta_gerencia", label: "Dirección / Alta Gerencia" },
  { value: "compras_abastecimiento", label: "Compras / Abastecimiento" },
  { value: "planeacion_estrategica", label: "Planeación Estratégica" },
  { value: "servicio_tecnico_mantenimiento", label: "Servicio Técnico / Mantenimiento" },
  { value: "community_management_redes", label: "Community Management / Redes Sociales" },
  { value: "gestion_ambiental", label: "Gestión Ambiental" },
  { value: "construccion_obras_civiles", label: "Construcción / Obras Civiles" },
  { value: "gestion_riesgos", label: "Gestión de Riesgos" },
  { value: "auditoria_control_interno", label: "Auditoría / Control Interno" },
  { value: "otra", label: "Otra" },
];

// Reglas de validación para los campos del formulario
const validationRules = {
  nombre_empresa: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ'\s\-\.&]+$/,
    messages: {
      required: "El nombre de la empresa es obligatorio",
      minLength: "El nombre debe tener al menos 2 caracteres",
      maxLength: "El nombre no puede exceder 100 caracteres",
      pattern: "Solo se permiten letras, espacios, guiones y puntos"
    }
  },
  identidad_empresa: {
    required: true,
    pattern: /^\d+$/,
    messages: {
      required: "El NIT es obligatorio",
      pattern: "El NIT debe ser un número válido"
    }
  },
  email: {
    required: true,
    pattern: /\S+@\S+\.\S+/,
    messages: {
      required: "El correo electrónico es obligatorio",
      pattern: "El correo electrónico no es válido"
    }
  },
  celular: {
    required: true,
    minLength: 7,
    maxLength: 15,
    pattern: /^\d+$/,
    messages: {
      required: "El celular es obligatorio",
      minLength: "El celular debe tener al menos 7 dígitos",
      maxLength: "El celular no puede exceder 15 dígitos",
      pattern: "El celular debe contener solo números"
    }
  },
  password: {
    required: true,
    minLength: 6,
    messages: {
      required: "La contraseña es obligatoria",
      minLength: "La contraseña debe tener al menos 6 caracteres"
    }
  },
  tamano: {
    required: true,
    messages: {
      required: "Debe seleccionar el tamaño de la empresa"
    }
  },
  sector: {
    required: true,
    messages: {
      required: "Debe seleccionar el tipo de empresa"
    }
  },
  pais: {
    required: true,
    messages: {
      required: "Debe seleccionar un país"
    }
  },
  departamento: {
    required: true,
    messages: {
      required: "Debe seleccionar un departamento"
    }
  },
  ciudad: {
    required: true,
    messages: {
      required: "Debe seleccionar una ciudad"
    }
  },
  terminos: {
    required: true,
    messages: {
      required: "Debes aceptar los términos y condiciones"
    }
  }
};

/**
 * Función para obtener el número completo con indicativo
 * @param {string} pais - País seleccionado
 * @param {string} numero - Número de celular sin indicativo
 * @returns {string} Número completo con indicativo
 */
const obtenerNumeroCompleto = (pais, numero) => {
  if (!pais || !numero) return numero;
  const indicativo = INDICATIVOS_PAIS[pais];
  return indicativo ? `${indicativo} ${numero}` : numero;
};

/**
 * Función para guardar el historial de aceptación de términos
 * @param {Object} datosEmpresa - Datos de la empresa que acepta términos
 */
const guardarHistorialTerminos = (datosEmpresa) => {
  try {
    // Obtener historial existente de términos
    const historialTerminos = JSON.parse(localStorage.getItem('historial_terminos') || '[]');
    
    // Crear nuevo registro de aceptación de términos
    const nuevaAceptacion = {
      id: Date.now(),
      fecha: new Date().toISOString(),
      fechaFormateada: new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      empresa: datosEmpresa.nombre_empresa || 'Empresa no especificada',
      email: datosEmpresa.email || 'Email no especificado',
      nit: datosEmpresa.nit || 'NIT no especificado',
      pais: datosEmpresa.pais_operacion || 'País no especificado',
      departamento: datosEmpresa.departamento_operacion || 'Departamento no especificado',
      ciudad: datosEmpresa.ciudad_operacion || 'Ciudad no especificada',
      ipAddress: 'N/A', // Se podría obtener la IP real si es necesario
      userAgent: navigator.userAgent,
      version_terminos: '1.0',
      metodo_aceptacion: 'modal', // Indica que se aceptó desde el modal
      estado: 'aceptado'
    };
    
    // Agregar al historial (mantener solo los últimos 100 registros)
    const nuevoHistorialTerminos = [nuevaAceptacion, ...historialTerminos].slice(0, 100);
    
    // Guardar en localStorage
    localStorage.setItem('historial_terminos', JSON.stringify(nuevoHistorialTerminos));
    
    console.log('Historial de términos guardado exitosamente:', nuevaAceptacion);
    return nuevaAceptacion;
  } catch (error) {
    console.error('Error al guardar historial de términos:', error);
    return null;
  }
};

/**
 * Función para guardar el historial de registro
 * @param {Object} registroData - Datos del registro exitoso
 * @param {string} numeroCompleto - Número completo con indicativo
 */
const guardarHistorialRegistro = (registroData, numeroCompleto) => {
  try {
    // Obtener historial existente
    const historialExistente = JSON.parse(localStorage.getItem('historial_registros') || '[]');
    
    // Crear nuevo registro de historial
    const nuevoRegistro = {
      id: Date.now(),
      fecha: new Date().toISOString(),
      fechaFormateada: new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      empresa: registroData.nombre_empresa,
      email: registroData.email,
      nit: registroData.nit,
      telefono: numeroCompleto, // Guardar número completo con indicativo
      telefonoLocal: registroData.telefono, // Guardar también el número local
      indicativo: INDICATIVOS_PAIS[registroData.pais_operacion] || '',
      pais: registroData.pais_operacion,
      departamento: registroData.departamento_operacion,
      ciudad: registroData.ciudad_operacion,
      sector: registroData.sector_empresa,
      tamano: registroData.tamaño_empresa,
      terminosAceptados: true,
      fechaAceptacionTerminos: new Date().toISOString(),
      estado: 'exitoso'
    };
    
    // Agregar al historial (mantener solo los últimos 50 registros)
    const nuevoHistorial = [nuevoRegistro, ...historialExistente].slice(0, 50);
    
    // Guardar en localStorage
    localStorage.setItem('historial_registros', JSON.stringify(nuevoHistorial));
    
    console.log('Historial de registro guardado exitosamente:', nuevoRegistro);
  } catch (error) {
    console.error('Error al guardar historial de registro:', error);
  }
};

/**
 * Componente principal del formulario de registro
 * @returns {JSX.Element} Formulario de registro de empresas
 */
function Cover() {
  // Estados para manejo de ubicación
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  // Estado principal del formulario
  const [formData, setFormData] = useState({
    nombre_empresa: '',
    identidad_empresa: '',
    email: '',
    celular: '',
    password: '',
    tamano: '',
    sector: '',
    pais: '',
    departamento: '',
    ciudad: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [errores, setErrores] = useState({});
  const [showAlert, setShowAlert] = useState(false);

  // Nuevos estados para validación
  const [fieldValidation, setFieldValidation] = useState({});
  const [fieldTouched, setFieldTouched] = useState({});
  const [currentAlert, setCurrentAlert] = useState(null);
  const [showValidationAlert, setShowValidationAlert] = useState(false);

  // Nuevos estados para mensaje de éxito
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Estados para el modal de términos y condiciones
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);

  const navigate = useNavigate();

  // Obtener indicativo del país seleccionado
  const indicativoPais = selectedCountry ? INDICATIVOS_PAIS[selectedCountry] : '';

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      pais: selectedCountry,
      departamento: selectedDepartment,
      ciudad: selectedCity
    }));
  }, [selectedCountry, selectedDepartment, selectedCity]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedDepartment("");
    setSelectedCity("");
    
    // Limpiar errores de celular cuando cambia el país
    if (errores.celular) {
      setErrores(prev => ({
        ...prev,
        celular: null
      }));
    }
  };

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    setSelectedCity(""); // Limpiar ciudad al cambiar departamento
  };

  // Modificar esta parte para obtener las ciudades del departamento seleccionado
  const availableDepartments = selectedCountry ? DEPARTMENTS_BY_COUNTRY[selectedCountry] || [] : [];
  const availableCities = selectedCountry && selectedDepartment ? 
    [...(CITIES_BY_DEPARTMENT[selectedCountry]?.[selectedDepartment] || []), "Otra"] : [];

  const validateField = useCallback((fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return { isValid: true, message: '' };

    let isValid = true;
    let message = '';

    if (rules.required && !value.trim()) {
      isValid = false;
      message = rules.messages.required;
    } else if (rules.minLength && value.length < rules.minLength) {
      isValid = false;
      message = rules.messages.minLength;
    } else if (rules.maxLength && value.length > rules.maxLength) {
      isValid = false;
      message = rules.messages.maxLength;
    } else if (rules.pattern && !rules.pattern.test(value)) {
      isValid = false;
      message = rules.messages.pattern;
    }

    return { isValid, message };
  }, []);

  const showValidationMessage = useCallback((field, validation) => {
    if (!validation.isValid) {
      setCurrentAlert({
        type: 'error',
        message: validation.message,
        field
      });
      setShowValidationAlert(true);
    }
  }, []);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    
    // Para el campo celular, solo permitir números
    if (field === 'celular') {
      const numericValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [field]: numericValue
      });
      
      const validation = validateField(field, numericValue);
      setFieldValidation(prev => ({
        ...prev,
        [field]: validation
      }));

      if (fieldTouched[field]) {
        showValidationMessage(field, validation);
      }
    } else {
      setFormData({
        ...formData,
        [field]: value
      });

      const validation = validateField(field, value);
      setFieldValidation(prev => ({
        ...prev,
        [field]: validation
      }));

      if (fieldTouched[field]) {
        showValidationMessage(field, validation);
      }
    }

    if (errores[field]) {
      setErrores({
        ...errores,
        [field]: null
      });
    }
  };

  const handleFieldBlur = (field) => () => {
    setFieldTouched(prev => ({
      ...prev,
      [field]: true
    }));

    const validation = fieldValidation[field];
    if (validation && !validation.isValid) {
      showValidationMessage(field, validation);
    }
  };

  const handleTermsChange = (e) => {
    const isChecked = e.target.checked;
    setAceptaTerminos(isChecked);
    
    // Si acepta términos, guardar historial
    if (isChecked) {
      const datosParaTerminos = {
        nombre_empresa: formData.nombre_empresa,
        email: formData.email,
        nit: formData.identidad_empresa,
        pais_operacion: formData.pais,
        departamento_operacion: formData.departamento,
        ciudad_operacion: formData.ciudad
      };
      guardarHistorialTerminos(datosParaTerminos);
    }
    
    if (errores.terminos) {
      setErrores({
        ...errores,
        terminos: null
      });
    }
  };

  const handleOpenTermsModal = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
    setAcceptingTerms(false);
  };

  const handleAcceptTermsFromModal = async () => {
    setAcceptingTerms(true);
    
    try {
      // Simular un pequeño delay para mostrar el loading
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Guardar historial de términos con datos disponibles
      const datosParaTerminos = {
        nombre_empresa: formData.nombre_empresa,
        email: formData.email,
        nit: formData.identidad_empresa,
        pais_operacion: formData.pais,
        departamento_operacion: formData.departamento,
        ciudad_operacion: formData.ciudad
      };
      
      const historialGuardado = guardarHistorialTerminos(datosParaTerminos);
      
      if (historialGuardado) {
        // Marcar términos como aceptados
        setAceptaTerminos(true);
        
        // Limpiar errores de términos
        if (errores.terminos) {
          setErrores(prev => ({
            ...prev,
            terminos: null
          }));
        }
        
        // Mostrar mensaje de éxito temporal
        setCurrentAlert({
          type: 'success',
          message: '¡Términos y condiciones aceptados exitosamente!'
        });
        setShowValidationAlert(true);
        
        // Cerrar modal
        setShowTermsModal(false);
      }
    } catch (error) {
      console.error('Error al aceptar términos:', error);
      setCurrentAlert({
        type: 'error',
        message: 'Error al procesar la aceptación de términos'
      });
      setShowValidationAlert(true);
    } finally {
      setAcceptingTerms(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombre_empresa.trim()) {
      newErrors.nombre_empresa = "El nombre de la empresa es requerido";
    }

    if (!formData.identidad_empresa.trim()) {
      newErrors.identidad_empresa = "El NIT es requerido";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El correo electrónico es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "El correo electrónico no es válido";
    }

    if (!formData.celular.trim()) {
      newErrors.celular = "El celular es requerido";
    } else if (formData.celular.length < 7) {
      newErrors.celular = "El celular debe tener al menos 7 dígitos";
    } else if (formData.celular.length > 15) {
      newErrors.celular = "El celular no puede exceder 15 dígitos";
    }

    if (!formData.password.trim()) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    if (!formData.tamano) {
      newErrors.tamano = "Debe seleccionar el tamaño de la empresa";
    }

    if (!formData.sector) {
      newErrors.sector = "Debe seleccionar el tipo de empresa";
    }

    if (!formData.pais) {
      newErrors.pais = "Debe seleccionar un país";
    }

    if (!formData.departamento) {
      newErrors.departamento = "Debe seleccionar un departamento";
    }

    if (!formData.ciudad) {
      newErrors.ciudad = "Debe seleccionar una ciudad";
    }

    if (!aceptaTerminos) {
      newErrors.terminos = "Debes aceptar los términos y condiciones";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);
    setShowAlert(false);
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrores(validationErrors);
      setLoading(false);
      return;
    }

    try {
      // Obtener número completo con indicativo
      const numeroCompleto = obtenerNumeroCompleto(formData.pais, formData.celular);
      
      const datosParaEnviar = {
        email: formData.email,
        password: formData.password,
        nombre_empresa: formData.nombre_empresa,
        nit: formData.identidad_empresa,
        telefono: numeroCompleto, // Enviar número completo con indicativo
        pais_operacion: formData.pais,
        departamento_operacion: formData.departamento,
        ciudad_operacion: formData.ciudad,
        tamaño_empresa: formData.tamano,
        sector_empresa: formData.sector
      };

      const resultado = await registerCompanyService.registerCompany(datosParaEnviar);
      console.log('Registro exitoso:', resultado);
      
      // Guardar historial de registro exitoso con número completo
      guardarHistorialRegistro(datosParaEnviar, numeroCompleto);
      
      // Mostrar mensaje de éxito
      setSuccessMessage(`¡Registro exitoso! Bienvenido ${formData.nombre_empresa}. Tu cuenta ha sido creada correctamente. Número registrado: ${numeroCompleto}`);
      setShowSuccessAlert(true);
      
      // Navegar después de mostrar el mensaje de éxito (delay de 4 segundos)
      setTimeout(() => {
        navigate('/authentication/sign-in');
      }, 4000);
      
    } catch (error) {
      setError(error.message || 'Error en el registro. Por favor, intente nuevamente.');
      setShowAlert(true);
      console.error('Error en el registro:', error);
      
    } finally {
      setLoading(false);
    }
  };

  const getFieldColor = (field) => {
    if (!fieldTouched[field]) return 'info';
    const validation = fieldValidation[field];
    return validation?.isValid ? 'success' : 'error';
  };

  const getValidationIcon = (field) => {
    if (!fieldTouched[field]) return null;
    const validation = fieldValidation[field];
    return validation?.isValid ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />;
  };

  return (
    <CoverLayout
      image={bgImage}
      customWidth={{
        xs: 11, sm: 10, md: 7, lg: 6, xl: 5
      }}
      sx={{
        background: "#9B5DE5",
        minHeight: "100vh",
      }}
    >
      {/* Alert de error */}
      <MDBox
        sx={{
          position: "fixed",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: "90%",
          maxWidth: "400px",
        }}
      >
        <Collapse in={showAlert}>
          <Alert 
            severity="error" 
            onClose={() => setShowAlert(false)}
            sx={{
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              borderRadius: "8px",
            }}
          >
            {error}
          </Alert>
        </Collapse>
      </MDBox>

      {/* Alert de éxito */}
      <MDBox
        sx={{
          position: "fixed",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10000,
          width: "90%",
          maxWidth: "600px",
        }}
      >
        <Collapse in={showSuccessAlert}>
          <Alert 
            severity="success" 
            onClose={() => setShowSuccessAlert(false)}
            sx={{
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              borderRadius: "8px",
              fontSize: "14px",
            }}
            icon={<CheckCircleIcon fontSize="inherit" />}
          >
            {successMessage}
          </Alert>
        </Collapse>
      </MDBox>

      {/* Modal de Términos y Condiciones */}
      <Dialog
        open={showTermsModal}
        onClose={handleCloseTermsModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: "#9B5DE5",
            color: "white",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.2rem"
          }}
        >
          Términos y Condiciones de Uso - Quadrante H
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "24px",
            backgroundColor: "#f8f9fa"
          }}
        >
          <MDBox
            sx={{
              whiteSpace: "pre-line",
              lineHeight: 1.6,
              fontSize: "14px",
              color: "#333",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "16px",
              backgroundColor: "white",
              borderRadius: "8px",
              border: "1px solid #e0e0e0"
            }}
          >
            {TERMINOS_Y_CONDICIONES}
          </MDBox>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#f8f9fa",
            justifyContent: "space-between"
          }}
        >
          <Button
            onClick={handleCloseTermsModal}
            variant="outlined"
            disabled={acceptingTerms}
            sx={{
              color: "#666",
              borderColor: "#666",
              "&:hover": {
                borderColor: "#333",
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            Cerrar
          </Button>
          <MDButton
            onClick={handleAcceptTermsFromModal}
            variant="gradient"
            color="info"
            disabled={acceptingTerms}
            sx={{
              background: (!acceptingTerms) ? "#9B5DE5" : "#FFFFFF",
              color: (!acceptingTerms) ? "#ffffff" : "#000000",
              cursor: (!acceptingTerms) ? "pointer" : "not-allowed",
              "&:hover": {
                background: (!acceptingTerms) ? "#139925ff" : "#FFFFFF",
                color: (!acceptingTerms) ? "#FFFFFF" : "#000000",
              },
              minWidth: "200px"
            }}
            startIcon={acceptingTerms ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {acceptingTerms ? "ACEPTANDO..." : "ACEPTO LOS TÉRMINOS"}
          </MDButton>
        </DialogActions>
      </Dialog>
      
      <Card>
        <MDBox
          variant="gradient"
          sx={{
            background: "#9B5DE5",
            borderRadius: "10px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            mx: 2,
            mt: -3,
            p: 2,
            mb: 1,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Inscríbete hoy a Quadrante H
          </MDTypography>
          <MDTypography display="block" variant="button" color="white" my={1}>
            Ingrese la siguiente información para registrarse
          </MDTypography>
        </MDBox>
        
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <MDInput 
                  type="text" 
                  label="Nombre de la Empresa" 
                  variant="outlined" 
                  fullWidth 
                  required
                  value={formData.nombre_empresa}
                  onChange={handleInputChange('nombre_empresa')}
                  onBlur={handleFieldBlur('nombre_empresa')}
                  error={!!errores.nombre_empresa}
                  helperText={errores.nombre_empresa}
                  color={getFieldColor('nombre_empresa')}
                  InputProps={{
                    endAdornment: getValidationIcon('nombre_empresa'),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput 
                  type="text" 
                  label="NIT / Número de Identificación" 
                  variant="outlined" 
                  fullWidth 
                  required
                  value={formData.identidad_empresa}
                  onChange={handleInputChange('identidad_empresa')}
                  onBlur={handleFieldBlur('identidad_empresa')}
                  error={!!errores.identidad_empresa}
                  helperText={errores.identidad_empresa}
                  color={getFieldColor('identidad_empresa')}
                  InputProps={{
                    endAdornment: getValidationIcon('identidad_empresa'),
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <MDInput 
                  type="email"
                  label="Correo electrónico" 
                  variant="outlined" 
                  fullWidth 
                  required
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  onBlur={handleFieldBlur('email')}
                  error={!!errores.email}
                  helperText={errores.email}
                  color={getFieldColor('email')}
                  InputProps={{
                    endAdornment: getValidationIcon('email'),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput 
                  type="text" 
                  label="Celular del responsable" 
                  variant="outlined" 
                  fullWidth 
                  required
                  value={formData.celular}
                  onChange={handleInputChange('celular')}
                  onBlur={handleFieldBlur('celular')}
                  error={!!errores.celular}
                  helperText={errores.celular || (indicativoPais && formData.celular ? `Número completo: ${indicativoPais} ${formData.celular}` : '')}
                  color={getFieldColor('celular')}
                  placeholder="Ej: 3001234567"
                  InputProps={{
                    startAdornment: indicativoPais ? (
                      <InputAdornment position="start">
                        <MDTypography 
                          variant="body2" 
                          color="text" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: '#9B5DE5',
                            minWidth: 'fit-content'
                          }}
                        >
                          {indicativoPais}
                        </MDTypography>
                      </InputAdornment>
                    ) : null,
                    endAdornment: getValidationIcon('celular'),
                  }}
                />
              </Grid>
            </Grid>

            <MDBox mb={2}>
              <MDInput 
                type="password"
                label="Contraseña" 
                variant="outlined" 
                fullWidth 
                required
                value={formData.password}
                onChange={handleInputChange('password')}
                onBlur={handleFieldBlur('password')}
                error={!!errores.password}
                helperText={errores.password}
                color={getFieldColor('password')}
                InputProps={{
                  endAdornment: getValidationIcon('password'),
                }}
              />
            </MDBox>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6}>
                <MDInput
                  select
                  label="Tamaño Empresa"
                  fullWidth
                  variant="outlined"
                  value={formData.tamano}
                  onChange={handleInputChange('tamano')}
                  error={!!errores.tamano}
                  helperText={errores.tamano}
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: formData.tamano ? 'left' : 'center',
                      padding: '16px 14px',
                      minHeight: '48px'
                    },
                    '& .MuiInputBase-root': {
                      minHeight: '56px'
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          zIndex: 9999,
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">Tamaño Empresa</MenuItem>
                  <MenuItem value="pequena">Pequeña</MenuItem>
                  <MenuItem value="mediana">Mediana</MenuItem>
                  <MenuItem value="grande">Grande</MenuItem>
                </MDInput>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput
                  select
                  label="Sector de la empresa"
                  fullWidth
                  variant="outlined"
                  value={formData.sector}
                  onChange={handleInputChange('sector')}
                  error={!!errores.sector}
                  helperText={errores.sector}
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: formData.sector ? 'left' : 'center',
                      padding: '16px 14px',
                      minHeight: '48px'
                    },
                    '& .MuiInputBase-root': {
                      minHeight: '56px'
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          zIndex: 9999,
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">Sector de la empresa</MenuItem>
                  {Nombre_area.map((companyType) => (
                    <MenuItem key={companyType.value} value={companyType.value}>
                      {companyType.label}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <CountrySelector
                  onSelect={handleCountrySelect}
                  selectedCountryValue={selectedCountry}
                  error={!!errores.pais}
                  helperText={errores.pais}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDInput
                  select
                  label="Departamento"
                  value={selectedDepartment}
                  onChange={(e) => handleDepartmentSelect(e.target.value)}
                  fullWidth
                  disabled={!selectedCountry}
                  variant="outlined"
                  error={!!errores.departamento}
                  helperText={errores.departamento}
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: selectedDepartment ? 'left' : 'center',
                      padding: '16px 14px',
                      minHeight: '48px'
                    },
                    '& .MuiInputBase-root': {
                      minHeight: '56px'
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          zIndex: 9999,
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">Seleccione Departamento</MenuItem>
                  {availableDepartments.map((department) => (
                    <MenuItem key={department} value={department}>
                      {department}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>

              <Grid item xs={12} sm={4}>
                <MDInput
                  select
                  label="Ciudad"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  fullWidth
                  disabled={!selectedDepartment}
                  variant="outlined"
                  error={!!errores.ciudad}
                  helperText={errores.ciudad}
                  sx={{
                    '& .MuiSelect-select': {
                      textAlign: selectedCity ? 'left' : 'center',
                      padding: '16px 14px',
                      minHeight: '48px'
                    },
                    '& .MuiInputBase-root': {
                      minHeight: '56px'
                    }
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: {
                          zIndex: 9999,
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">Seleccione Ciudad</MenuItem>
                  {availableCities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>
            </Grid>

            <MDBox display="flex" alignItems="center" ml={-1} mb={2}>
              <Checkbox 
                checked={aceptaTerminos}
                onChange={handleTermsChange}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Estoy de acuerdo con &nbsp;
              </MDTypography>
              <MDTypography
                component="a"
                href="#"
                variant="button"
                fontWeight="bold"
                color="info"
                textGradient
                onClick={handleOpenTermsModal}
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    color: "#9B5DE5"
                  }
                }}
              >
                Términos y condiciones
              </MDTypography>
            </MDBox>
            {errores.terminos && (
              <MDTypography variant="caption" color="error">
                {errores.terminos}
              </MDTypography>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton 
                variant="gradient" 
                color="info" 
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  background: (!loading) ? "#9B5DE5" : "#FFFFFF",
                  color: (!loading) ? "#ffffffff" : "#000000",
                  cursor: (!loading) ? "pointer" : "not-allowed",
                  "&:hover": {
                    background: (!loading) ? "#139925ff" : "#FFFFFF",
                    color: (!loading) ? "#FFFFFF" : "#000000",
                  },
                }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? "REGISTRANDO..." : "REGISTRARSE"}
              </MDButton>
            </MDBox>

            <MDBox textAlign="center" mt={3} mb={4}>
              <MDTypography variant="caption" color="text" fontWeight="regular">
                Un producto de Koop Tecnológica SAS BIC
              </MDTypography>
            </MDBox>

            <MDBox mt={2} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                ¿Ya tienes una cuenta?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  sx={{
                    color: "#9B5DE5",
                    fontWeight: "medium",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Iniciar sesión
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Snackbar
        open={showValidationAlert}
        autoHideDuration={4000}
        onClose={() => setShowValidationAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 10000 }}
      >
        <Alert
          severity={currentAlert?.type || 'info'}
          onClose={() => setShowValidationAlert(false)}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setShowValidationAlert(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            borderRadius: "8px",
            minWidth: "300px"
          }}
        >
          {currentAlert?.message}
        </Alert>
      </Snackbar>
    </CoverLayout>
  );
}

export default Cover;